import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { placeTooltip, type TooltipPlacement } from "./tooltipPlacement";

interface Props {
  /** Styled tooltip body shown on hover. */
  content: ReactNode;
  /** The trigger element(s) the tooltip is anchored to. */
  children: ReactNode;
  /** Extra class on the inline trigger wrapper. */
  className?: string;
}

/**
 * Hover tooltip whose body is rendered in a portal on document.body with fixed coordinates, so it
 * escapes ancestor `overflow: hidden/auto` clipping (e.g. the scrollable requests table). Placement
 * is shared with `InfoTooltip` (tooltipPlacement.ts): centred on the trigger, clamped to the
 * viewport, flipped above when there is no room below.
 *
 * Hover-only by design: triggers that use this (e.g. SlownessPill) already carry a descriptive
 * `aria-label`, so the tooltip is a visual enhancement and the body is `aria-hidden` to avoid
 * duplicate screen-reader output. Not a keyboard tab stop — keeps dense tables navigable.
 */
export function Tooltip({ content, children, className }: Props) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bodyRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<TooltipPlacement | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const anchor = triggerRef.current?.getBoundingClientRect();
    const body = bodyRef.current;
    if (!anchor || !body) return;
    setPos(
      placeTooltip(
        anchor,
        { width: body.offsetWidth, height: body.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  }, [open]);

  return (
    <span
      ref={triggerRef}
      className={"tt-trigger" + (className ? " " + className : "")}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {children}
      {open &&
        createPortal(
          <span
            ref={bodyRef}
            role="tooltip"
            aria-hidden="true"
            className={"tt-pop" + (pos ? ` is-open is-${pos.side}` : "")}
            style={
              pos
                ? ({ top: pos.top, left: pos.left, "--tt-arrow-x": `${pos.arrowLeft}px` } as CSSProperties)
                : undefined
            }
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
}
